import { useEffect, useRef, useState } from "react";
import { api, ApiError, type CommandStatus } from "../lib/api";
import { useDraft } from "../lib/drafts";
import { API_BASE } from "../lib/base";
import { useConversationDrawn } from "../lib/conversations";
import { SurfaceAction } from "./SurfaceAction";

export interface SurfaceFault { detail: Record<string, unknown>; version: string }
interface Attempt { command: string; obligation?: string; session?: string; version: string; automatic?: boolean }

/** Runtime errors notify the original Agent once; Core receipts outlive the tab. */
export function SurfaceRepair({ panelKey, index, context, fault, version, appliedVersion, active, onToast, onVerified }: {
  panelKey: string; index: number; context: Record<string, string>; fault: SurfaceFault;
  version: string; appliedVersion: string; active: boolean;
  onToast: (text: string, tone: "good" | "critical") => void; onVerified: () => void;
}) {
  const normalizedContext = { ...Object.fromEntries(Object.entries(context).filter(([key]) => key !== "session_id" && key !== "session").sort()),
    session: context.session || context.session_id || "s-owner" };
  const contextKey = JSON.stringify(normalizedContext);
  const scope = `${panelKey}:${index}:${contextKey}`;
  const currentScope = useRef(scope); currentScope.current = scope;
  const [attempt, setAttempt, cacheError] = useDraft<Attempt | null>(`surface:${scope}:repair`, null);
  const [status, setStatus] = useState<CommandStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<{ scope: string; reason: string } | null>(null);
  const [sending, setSending] = useState(false);
  // The repair is asked for in a conversation; the link to it is drawn only where the owner can read it.
  const repairDrawn = useConversationDrawn(attempt?.obligation ? attempt.session || "s-owner" : undefined);
  const busy = useRef<string | null>(null);
  const recovered = useRef("");
  const automatic = fault.detail.kind === "error" || fault.detail.kind === "unhandledrejection";
  const changed = Boolean(attempt && version && version !== attempt.version);
  const applied = changed && version === appliedVersion;
  const failedAgain = Boolean(applied && fault.version === appliedVersion);
  const ended = Boolean(status?.known && (status.action_state === "completed" || status.action_state === "stopped" || status.action_state === "failed" || status.action_state === "unknown"));
  const submit = async (recover = false) => {
    if (busy.current === scope) return;
    busy.current = scope; setSending(true); setError(null);
    // Automatic reporting first recovers an already accepted request on the
    // server. An uncertain retry retains the same command and origin.
    const next: Attempt = recover ? { command: "", version: appliedVersion, automatic: true } : attempt && !attempt.obligation ? attempt : {
      command: `surface-repair-${crypto.randomUUID()}`, version: appliedVersion, automatic: false,
    };
    try {
      setAttempt(next);
      const result = await api.repairSurface(panelKey, index, normalizedContext, fault.detail, next.command || undefined, next.automatic === true);
      if (currentScope.current !== scope) return;
      if ("repairable" in result) {
        setAttempt(null);
        setUnavailable({ scope, reason: result.reason });
        return;
      }
      setUnavailable(null);
      setAttempt({ ...next, command: result.command_id, obligation: result.obligation_id, session: result.session_id,
        version: result.version || next.version });
      setStatus(null);
    } catch (err) { if (currentScope.current === scope) setError(ApiError.fromUnknown(err).detail); }
    finally { if (busy.current === scope) busy.current = null; if (currentScope.current === scope) setSending(false); }
  };
  useEffect(() => {
    if (!active || !automatic || recovered.current === scope) return;
    recovered.current = scope;
    void submit(true);
  }, [active, automatic, scope]);
  if (unavailable?.scope === scope) return <section className="surface-repair" aria-label="界面修复">
    <p role="status"><strong>这块界面需要更新修复</strong></p>
    <p>{unavailable.reason}</p>
  </section>;
  return <section className="surface-repair" aria-label="界面修复">
    <p role="status"><strong>{failedAgain ? "新版本仍有报错" : applied ? "已应用新版本，等待你验证" : changed
      ? "发现界面更新，应用后请验证" : ended ? "本轮处理已结束，尚未发现可用的界面更新" : attempt?.obligation
      ? "已通知 Agent 修复" : automatic ? "正在通知 Agent 修复" : "这块界面遇到了问题"}</strong></p>
    <p>{String(fault.detail.message || "界面没有正常运行")}</p>
    {automatic ? <p className="micro">修复会接回原对话，已保存的内容会保留。</p> : !attempt?.obligation ? <p className="micro">把问题交回原 Agent，继续处理这块界面。</p> : null}
    {error || cacheError ? <p role="alert">{error || cacheError}</p> : null}
    {(!automatic && !attempt?.obligation) || error || ended ? <button type="button" className="btn" disabled={sending}
      onClick={() => void submit()}>{sending ? "正在提交修复请求" : attempt && !attempt.obligation ? "重试发送同一请求" : attempt?.obligation ? "让原 Agent 再处理一次" : "让原 Agent 修复"}</button> : null}
    {applied && !failedAgain ? <button type="button" className="btn" onClick={() => { setAttempt(null); onVerified(); }}>已验证可用</button> : null}
    {attempt?.obligation ? <>
      {repairDrawn === false ? null
        : repairDrawn === undefined ? <a className="surface-repair-link" aria-hidden="true" tabIndex={-1}>查看这位 Agent 的修复对话</a>
          : <a className="surface-repair-link" href={`${API_BASE}/#/chat?session=${encodeURIComponent(attempt.session || "s-owner")}`} target="_blank" rel="noreferrer">查看这位 Agent 的修复对话</a>}
      <SurfaceAction key={attempt.obligation} submission={{ id: attempt.obligation, label: "修复请求" }} active={active}
        onStatus={setStatus} onToast={onToast} />
    </> : null}
  </section>;
}
